import { z } from 'zod'
import { eventPriceInfoSchema } from '../validation'
import { eventTicketUrlKindValues } from './event'

export const eventToneSchema = z.enum(['friendly', 'professional', 'bold']).default('friendly')
export type EventTone = z.infer<typeof eventToneSchema>

export const eventLengthSchema = z.enum(['short', 'standard', 'detailed']).default('standard')
export type EventLength = z.infer<typeof eventLengthSchema>

export const generateEventRequestSchema = z.object({
  prompt: z.string().min(1).max(2000),
  details: z
    .object({
      date: z.string().optional(),
      time: z.string().optional(),
      price: z.string().optional(),
      capacity: z.string().optional(),
    })
    .optional(),
  tone: eventToneSchema,
  length: eventLengthSchema,
  locale: z.enum(['de', 'en']).default('de'),
})

export type GenerateEventRequest = z.infer<typeof generateEventRequestSchema>

const generatedTagsSchema = z.object({
  mainCategoryId: z.string(),
  subCategoryTags: z.array(z.string()),
  subSubCategoryTags: z.array(z.string()),
  amenityIds: z.array(z.string()),
})

/** Where the event takes place, as read from the sources and placed on the map when possible. */
export const generatedEventLocationSchema = z.object({
  name: z.string().nullable(),
  street: z.string(),
  city: z.string(),
  country: z.string(),
  coordinates: z.object({ lat: z.number(), lng: z.number() }).nullable(),
})
export type GeneratedEventLocation = z.infer<typeof generatedEventLocationSchema>

/** A ticket category a source names, in cents; a door price only on a free reservation. */
export const draftTicketTypeSchema = z.object({
  name: z.string(),
  priceCents: z.number().int().min(0),
  isStudent: z.boolean(),
  doorPriceCents: z.number().int().min(0).nullable(),
})
export type DraftTicketType = z.infer<typeof draftTicketTypeSchema>

/** The event happens again, e.g. every Thursday: `count` dates in all. */
export const draftRepeatSchema = z.object({
  every: z.enum(['day', 'week', 'month']),
  interval: z.number().int().min(1).max(12),
  count: z.number().int().min(2).max(52),
})
export type DraftRepeat = z.infer<typeof draftRepeatSchema>

export const draftVisibilityValues = ['public', 'request', 'private'] as const
export type DraftVisibility = (typeof draftVisibilityValues)[number]

export const generateEventResponseSchema = z.object({
  title: z.string(),
  description: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  price: eventPriceInfoSchema,
  tags: generatedTagsSchema,
  ticketUrl: z.string().nullable(),
  capacity: z.number().int().nullable(),
  /** Null when no source names a place. */
  location: generatedEventLocationSchema.nullable().default(null),
  /** The business's own listing when the event takes place there. */
  listingId: z.string().nullable().default(null),
  /** A source says guests have to sign up in advance. */
  signupRequired: z.boolean().default(false),
  /** What `ticketUrl` is for, when a source tells; null without a link or when unclear. */
  ticketUrlKind: z.enum(eventTicketUrlKindValues).nullable().default(null),
  ticketTypes: z.array(draftTicketTypeSchema).default([]),
  visibility: z.enum(draftVisibilityValues).default('public'),
  minAge: z
    .union([z.literal(16), z.literal(18)])
    .nullable()
    .default(null),
  studentsOnly: z.boolean().default(false),
  targetAgeMin: z.number().int().nullable().default(null),
  targetAgeMax: z.number().int().nullable().default(null),
  /** A short note for guests, such as a dress code. */
  audienceNote: z.string().nullable().default(null),
  /** A source says the venue is announced later. */
  locationPending: z.boolean().default(false),
  /** A source says only registered guests or ticket holders get the exact address. */
  addressHidden: z.boolean().default(false),
  repeat: draftRepeatSchema.nullable().default(null),
  coverImageBase64: z.string(),
  coverMimeType: z.string(),
})

export type GenerateEventResponse = z.infer<typeof generateEventResponseSchema>

/** Credit cost of filling in an event from a sentence, a link or a flyer, without a cover. */
export const EVENT_DRAFT_CREDIT_COST = 300
export const EVENT_DRAFT_MAX_IMAGES = 3
export const EVENT_DRAFT_MAX_IMAGE_BYTES = 4 * 1024 * 1024
export const EVENT_DRAFT_MAX_PROMPT = 2000

const EVENT_DRAFT_LINK = /https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)\]]/i

/** The first link in what the business typed, and the rest of the text without it. */
export function splitEventDraftPrompt(raw: string): { url: string | null; text: string } {
  const url = EVENT_DRAFT_LINK.exec(raw)?.[0] ?? null
  const text = (url ? raw.replace(url, ' ') : raw)
    .replace(/\s+/g, ' ')
    .replace(/ ([.,;:!?])/g, '$1')
    .trim()
  return { url, text }
}

/** The steps a flow still gets without paying again. */
export const eventFlowStateSchema = z.object({
  flowId: z.string().uuid(),
  package: z.boolean(),
  free: z.object({
    cover: z.boolean(),
    translation: z.boolean(),
    /** Chosen variants that are finished in high quality at no extra cost. */
    finalize: z.number().int().nonnegative(),
    /** Channel formats beyond the Pulsify cover that the package still includes. */
    formats: z.number().int().nonnegative(),
  }),
})
export type EventFlowState = z.infer<typeof eventFlowStateSchema>

/** An event filled in from what the business gave: no cover is generated. */
export const eventDraftResponseSchema = generateEventResponseSchema
  .omit({
    coverImageBase64: true,
    coverMimeType: true,
  })
  .extend({
    /** The flow run this draft started, for the cover, translation and package steps. */
    flowId: z.string().uuid().nullable().default(null),
    flow: eventFlowStateSchema.nullable().default(null),
  })

export type EventDraftResponse = z.infer<typeof eventDraftResponseSchema>
